import { mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import { Action, EventType, ObjectType, TeamSocketData } from 'api/pushes';
import { TaskAPI } from 'api/tasks';
import { RootState } from 'data';
import { FetchStatus } from 'data/core';
import { Task } from 'data/tasks';
import { taskNormalizer } from 'data/tasks/schema';
import { saveNormalizedTask } from 'data/tasks/slice';
import { useTeamWsUpdates } from 'hooks/use-team-ws-updates';
import { getProjectTasksQueryKey } from 'queries/common';
import { TaskField } from 'stores/fields';
import { loadedProjectTasks } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { toStoreTask } from 'utils/to-store-task';

// @TODO handle indexedDb update here to keep project related changes in sync
export const useProjectPlaceholderWebsocket = (
  _projectId?: ProjectId,
): void => {
  const [, projectId] = _projectId || [];
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const fields_lookup = useMemo(
    () =>
      mapValues(fieldsStore.byId, ([, x]) => x!) as Record<number, TaskField>,
    [fieldsStore.byId],
  );

  const handleTeamSocketUpdate = useCallback(
    async (socketData: TeamSocketData<Task>) => {
      if (
        !socketData?.projectId ||
        (projectId && socketData?.projectId !== projectId)
      ) {
        return;
      }
      switch (socketData.action) {
        case Action.Update:
          {
            queryClient.invalidateQueries(
              getProjectTasksQueryKey(ProjectId(socketData.projectId)),
            );

            const apiTasks = await TaskAPI.getTaskList(socketData.projectId);
            const updatedData = apiTasks.map((task) => ({
              ...task,
              fields: task.fields.map(withValidCustomFieldsForData),
            }));

            const {
              entities: { tasks },
            } = taskNormalizer(updatedData);
            const to_row = toStoreTask(fields_lookup, tasks);

            /** tasks_v2 update */
            dispatch(
              loadedProjectTasks([
                ProjectId(socketData.projectId),
                Object.values(tasks).map(to_row),
              ]),
            );

            /** tasks update */
            dispatch(
              saveNormalizedTask({
                data: tasks,
                fetchStatus: FetchStatus.Loaded,
              }),
            );
          }

          break;

        default:
      }
    },
    [dispatch, fields_lookup, projectId, queryClient],
  );

  useTeamWsUpdates(
    handleTeamSocketUpdate,
    EventType(Action.Update, ObjectType.Team),
  );
};
